import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getStoredToken, storeTokens, storeAdminToken } from '@core/api/token-storage';

/**
 * User interface matching the backend User model
 */
export interface User {
    id: number | string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string | null | undefined;
    profileImage?: string | null | undefined;
    dateOfBirth?: string | null | undefined;
    gender?: string | null | undefined;
    createdAt?: string | null | undefined;
}

/**
 * Admin user interface matching the backend Admin model
 */
export interface AdminUser {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isActive: boolean;
    isLocked: boolean;
    lastLoginDate?: string;
    lastLoginIP?: string;
    profileImageUrl?: string;
    createdAt?: string;
    permissions?: { [key: string]: boolean };
}

/**
 * Auth state interface
 */
interface AuthState {
    user: User | null;
    adminUser: AdminUser | null;
    userAccessToken: string | null;
    adminAccessToken: string | null;
    userRefreshToken: string | null;
    adminRefreshToken: string | null;
    isUserAuthenticated: boolean;
    isAdminAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
}

/**
 * Helper to check if a JWT is valid and not expired
 */
const isTokenValid = (token: string | null): boolean => {
    if (!token) return false;
    try {
        // Handle base64url encoding correctly for browsers
        const base64Url = token.split('.')[1];
        if (!base64Url) return false;

        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );

        const payload = JSON.parse(jsonPayload);
        // payload.exp is in seconds
        if (payload.exp && payload.exp * 1000 < Date.now()) {
            return false;
        }
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Initialize state from localStorage
 */
const getInitialState = (): AuthState => {
    const userToken = getStoredToken();
    const adminToken = localStorage.getItem('admin_token');

    const isUserTokenValid = isTokenValid(userToken);
    const isAdminTokenValid = isTokenValid(adminToken);

    // If tokens exist but are invalid/expired, we optionally clear them.
    // However, since we now have a refresh token mechanism, we might want to let the
    // interceptor handle the refresh. BUT, the frontend login page guard uses this state.
    // If we set isAuthenticated: false, they stay on the login page.
    // If they try to go to a protected page, the guard blocks them.
    // If we want to allow auto-refresh on page load, we would need to check if we have a refresh token.
    // To fix the specific bug: if the access token is expired, we should not consider them authenticated yet.
    // The Axios interceptor will refresh it when they make their first API call ONLY IF they are allowed
    // past the guard. But if the guard blocks them, they can't make API calls.
    // Let's rely on the refreshToken existence as a fallback to consider them potentially authenticated.

    // Instead of deep changes to the refresh cycle, let's just use the existence of the refresh token
    // If they have no valid access token and no refresh token, they are NOT authenticated.

    const userRefreshToken = localStorage.getItem('refresh_token');
    const adminRefreshToken = localStorage.getItem('admin_refresh_token');

    const storedAdmin = localStorage.getItem('currentAdmin');
    const storedUser = localStorage.getItem('currentUser');

    const isUserAuth = isUserTokenValid || (!!userToken && !!userRefreshToken);
    const isAdminAuth = isAdminTokenValid || (!!adminToken && !!adminRefreshToken);

    return {
        user: storedUser ? JSON.parse(storedUser) : null,
        adminUser: storedAdmin ? JSON.parse(storedAdmin) : null,
        userAccessToken: userToken,
        adminAccessToken: adminToken,
        userRefreshToken: userRefreshToken,
        adminRefreshToken: adminRefreshToken,
        isUserAuthenticated: isUserAuth,
        isAdminAuthenticated: isAdminAuth,
        isLoading: false,
        error: null,
    };
};

/**
 * Auth slice for user and admin authentication state
 */
const authSlice = createSlice({
    name: 'auth',
    initialState: getInitialState(),
    reducers: {
        /**
         * Start auth loading state
         */
        authStart: (state) => {
            state.isLoading = true;
            state.error = null;
        },

        /**
         * Successful user login
         */
        loginSuccess: (
            state,
            action: PayloadAction<{
                user: User;
                accessToken: string;
                refreshToken?: string | undefined;
            }>
        ) => {
            const { user, accessToken, refreshToken } = action.payload;

            state.user = user;
            state.userAccessToken = accessToken;
            state.userRefreshToken = refreshToken ?? null;
            state.isUserAuthenticated = true;
            state.isLoading = false;
            state.error = null;

            storeTokens(accessToken, refreshToken);
            localStorage.setItem('currentUser', JSON.stringify(user));
        },

        /**
         * Successful admin login
         */
        adminLoginSuccess: (
            state,
            action: PayloadAction<{
                adminUser: AdminUser;
                token: string;
                refreshToken?: string;
            }>
        ) => {
            const { adminUser, token, refreshToken } = action.payload;

            state.adminUser = adminUser;
            state.adminAccessToken = token;
            state.adminRefreshToken = refreshToken ?? null;
            state.isAdminAuthenticated = true;
            state.isLoading = false;
            state.error = null;

            storeAdminToken(token);
            if (refreshToken) {
                localStorage.setItem('admin_refresh_token', refreshToken);
            }
            localStorage.setItem('currentAdmin', JSON.stringify(adminUser));
        },

        /**
         * Auth failure
         */
        authFailure: (state, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        /**
         * Update tokens (e.g., after refresh)
         */
        setTokens: (
            state,
            action: PayloadAction<{
                accessToken: string;
                refreshToken?: string | undefined;
                isAdmin?: boolean;
            }>
        ) => {
            const { accessToken, refreshToken, isAdmin } = action.payload;

            if (isAdmin) {
                state.adminAccessToken = accessToken;
                if (refreshToken) {
                    state.adminRefreshToken = refreshToken;
                    localStorage.setItem('admin_refresh_token', refreshToken);
                }
                storeAdminToken(accessToken);
            } else {
                state.userAccessToken = accessToken;
                if (refreshToken) {
                    state.userRefreshToken = refreshToken;
                }
                storeTokens(accessToken, refreshToken);
            }
        },

        /**
         * Update user profile
         */
        updateProfile: (state, action: PayloadAction<Partial<User>>) => {
            if (state.user) {
                state.user = { ...state.user, ...action.payload };
                localStorage.setItem('currentUser', JSON.stringify(state.user));
            }
        },

        /**
         * Update admin profile
         */
        updateAdminProfile: (state, action: PayloadAction<Partial<AdminUser>>) => {
            if (state.adminUser) {
                state.adminUser = { ...state.adminUser, ...action.payload };
                localStorage.setItem('currentAdmin', JSON.stringify(state.adminUser));
            }
        },

        /**
         * Logout - clear all auth state
         */
        logout: (state) => {
            state.user = null;
            state.userAccessToken = null;
            state.userRefreshToken = null;
            state.isUserAuthenticated = false;
            state.isLoading = false;
            state.error = null;

            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('currentUser');
        },

        /**
         * Logout Admin - only clear admin state
         */
        adminLogout: (state) => {
            state.adminUser = null;
            state.adminAccessToken = null;
            state.adminRefreshToken = null;
            state.isAdminAuthenticated = false;
            state.isLoading = false;
            state.error = null;

            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_refresh_token');
            localStorage.removeItem('currentAdmin');
        },

        /**
         * Clear error state
         */
        clearError: (state) => {
            state.error = null;
        },
    },
});

export const {
    authStart,
    loginSuccess,
    adminLoginSuccess,
    authFailure,
    setTokens,
    updateProfile,
    updateAdminProfile,
    logout,
    adminLogout,
    clearError,
} = authSlice.actions;

export default authSlice.reducer;
